<script setup>
  import {computed, nextTick, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentRiskDonutChart",
  });

  const props = defineProps({
    embeddedInRow: {
      type: Boolean,
      default: false,
    },
    medications: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });
  const INTERACTION_CHECK = 1;

  const refDialog = ref(null);

  const showSubTitle = ref(false);
  const donutRadius = 30;
  const donutCenter = 50; // viewBox is 100x100, so center is at 50,50

  const configData = {
    veryhighrisk: true,
    highrisk: true,
    midrisk: true,
    lowrisk: true,
  };

  const riskClasses = {
    veryHighRisk: "red",
    highRisk: "orange",
    mediumRisk: "yellow",
    lowRisk: "grey",
    notShownChecks: "notShownChecks",
  };

  const totalMedicationRiskClasses = computed(() => {
    let totalMedicationRisks = [];

    props.medications.forEach((medication) => {
      let totalRisks = [];
      let color = "";

      Object.entries(medication.statistic ?? []).forEach(([key, statsData]) => {
        switch (key) {
          case "veryHighRisk": {
            color = "rot";

            break;
          }
          case "highRisk": {
            color = "orange";

            break;
          }
          case "mediumRisk": {
            color = "gelb";

            break;
          }
          case "lowRisk": {
            color = "grau";

            break;
          }
          // No default
        }

        if (key !== "skipped") {
          statsData.forEach((statsNode) => {
            totalRisks.push({
              checkName: statsNode.name,
              count: statsNode.count,
              color: color,
              riskClass: key,
              id: statsNode.id,
            });
          });
        }
      });

      totalMedicationRisks.push({
        famName: medication.fam,
        risks: totalRisks,
      });

      if (totalRisks.length === 0) {
        showSubTitle.value = true;
      }
    });

    return totalMedicationRisks;
  });

  const medicationRiskClassesWithRisks = computed(() => {
    return totalMedicationRiskClasses.value.filter((medication) => medication.risks.length > 0);
  });

  const medicationRiskClassesWithoutRisks = computed(() => {
    return totalMedicationRiskClasses.value.filter((medication) => medication.risks.length === 0);
  });

  const famsWithChecks = computed(() => {
    return medicationRiskClassesWithRisks.value.length;
  });

  const showDonut = computed(() => {
    return props.medications.length > 0 && famsWithChecks.value > 0 && !props.disabled;
  });

  const totalRiskPercents = computed(() => {
    let veryHighRisk = 0;
    let highRisk = 0;
    let mediumRisk = 0;
    let lowRisk = 0;

    // TODO: rework to const - the five declarations below are assigned exactly once
    //  after the forEach, so they belong at their assignment site
    let totalNumber;

    let veryHighRiskPercent;
    let highRiskPercent;
    let mediumRiskPercent;
    let lowRiskPercent;

    props.medications.forEach((medication) => {
      // Stats not filled
      if (medication.statistic === null || medication.statistic.length === 0) {
        return;
      }

      // Add only the half count of interaction check.
      // Because each interaction claim involves two
      // medications!
      if (medication.statistic["veryHighRisk"]) {
        medication.statistic["veryHighRisk"].forEach((check) => {
          if (check.id === INTERACTION_CHECK) {
            veryHighRisk += check.count / 2;
          } else {
            veryHighRisk += check.count;
          }
        });
      }

      if (medication.statistic["highRisk"]) {
        medication.statistic["highRisk"].forEach((check) => {
          if (check.id === INTERACTION_CHECK) {
            highRisk += check.count / 2;
          } else {
            highRisk += check.count;
          }
        });
      }

      if (medication.statistic["mediumRisk"]) {
        medication.statistic["mediumRisk"].forEach((check) => {
          if (check.id === INTERACTION_CHECK) {
            mediumRisk += check.count / 2;
          } else {
            mediumRisk += check.count;
          }
        });
      }

      if (medication.statistic["lowRisk"]) {
        medication.statistic["lowRisk"].forEach((check) => {
          if (check.id === INTERACTION_CHECK) {
            lowRisk += check.count / 2;
          } else {
            lowRisk += check.count;
          }
        });
      }
    });

    // Normalize values to avoid decimal numbers
    veryHighRisk = Math.floor(veryHighRisk);
    highRisk = Math.floor(highRisk);
    mediumRisk = Math.floor(mediumRisk);
    lowRisk = Math.floor(lowRisk);

    totalNumber = veryHighRisk + highRisk + mediumRisk + lowRisk;

    veryHighRiskPercent = totalNumber !== 0 ? veryHighRisk / totalNumber : -1;
    highRiskPercent = totalNumber !== 0 ? highRisk / totalNumber : -1;
    mediumRiskPercent = totalNumber !== 0 ? mediumRisk / totalNumber : -1;
    lowRiskPercent = totalNumber !== 0 ? lowRisk / totalNumber : -1;

    return {
      veryHighRisk: veryHighRiskPercent,
      highRisk: highRiskPercent,
      mediumRisk: mediumRiskPercent,
      lowRisk: lowRiskPercent,
      totalNumber: totalNumber,
    };
  });

  const veryHighRiskDashArray = computed(() => {
    // circum ference = 2 * pi * radius
    // x percent of circum ference = 2 * pi * r * (x / 100)
    return [2 * Math.PI * donutRadius * totalRiskPercents.value.veryHighRisk, 2 * Math.PI * donutRadius];
  });

  const veryHighRiskRotation = computed(() => {
    // offset = 0
    return "rotate(" + calculateRotationAngle(0) + " " + donutCenter + " " + donutCenter + ")";
  });

  const highRiskDashArray = computed(() => {
    // circum ference = 2 * pi * radius
    // x percent of circum ference = 2 * pi * r * (x / 100)
    return [2 * Math.PI * donutRadius * totalRiskPercents.value.highRisk, 2 * Math.PI * donutRadius];
  });

  const highRiskRotation = computed(() => {
    // offset = veryHighRiskPercentage
    return (
      "rotate(" +
      calculateRotationAngle(totalRiskPercents.value.veryHighRisk) +
      " " +
      donutCenter +
      " " +
      donutCenter +
      ")"
    );
  });

  const mediumRiskDashArray = computed(() => {
    // circum ference = 2 * pi * radius
    // x percent of circum ference = 2 * pi * r * (x / 100)
    return [2 * Math.PI * donutRadius * totalRiskPercents.value.mediumRisk, 2 * Math.PI * donutRadius];
  });

  const mediumRiskRotation = computed(() => {
    // offset = veryHighRiskPercentage + highRiskPercentage
    return (
      "rotate(" +
      calculateRotationAngle(totalRiskPercents.value.veryHighRisk + totalRiskPercents.value.highRisk) +
      " " +
      donutCenter +
      " " +
      donutCenter +
      ")"
    );
  });

  const lowRiskDashArray = computed(() => {
    // circum ference = 2 * pi * radius
    // x percent of circum ference = 2 * pi * r * (x / 100)
    return [2 * Math.PI * donutRadius * totalRiskPercents.value.lowRisk, 2 * Math.PI * donutRadius];
  });

  const lowRiskRotation = computed(() => {
    // offset = veryHighRiskPercentage + highRiskPercentage + mediumRiskPercentage
    return (
      "rotate(" +
      calculateRotationAngle(
        totalRiskPercents.value.veryHighRisk + totalRiskPercents.value.highRisk + totalRiskPercents.value.mediumRisk,
      ) +
      " " +
      donutCenter +
      " " +
      donutCenter +
      ")"
    );
  });

  function calculateRotationAngle(offsetPercentage) {
    return (offsetPercentage * 100 * 360) / 100 - 90;
  }

  function openRiskDialog() {
    refDialog.value.open();
  }

  function goToAnchor(checkId, risk) {
    let claimClass = riskClasses[risk];

    // close layer
    refDialog.value.close();

    let target = document.querySelector(`a[data-risk-goto-target='${claimClass}-${checkId}']`);

    // toggle open (note: aria-expanded value ist a string!)
    if (target.getAttribute("aria-expanded") === "false") {
      target.click();
    }

    nextTick(() => {
      // scroll into view
      target.scrollIntoView();

      if (target.getBoundingClientRect().top < 120) {
        const offset = 120; // offset of fixed header height
        window.scrollBy(0, -offset);
      }

      // highlight with glow animation
      target.closest("li").classList.add("animate-glow");
      setTimeout(() => {
        target.closest("li").classList.remove("animate-glow");
      }, 2000);
    });
  }
</script>

<template>
  <button v-if="showDonut" :data-test="getTestId('risk-donut-button')" @click="openRiskDialog">
    <MedTooltip
      content="Angezeigt werden die Anzahl aller arzneimittelbezogener Check-Treffer sowie deren Risikofarben. Hinweis: Interaktionen werden nicht mehrfach gezählt."
    >
      <svg width="50px" height="50px" viewBox="0 0 100 100" class="cursor-pointer">
        <circle
          :cx="donutCenter"
          :cy="donutCenter"
          :r="donutRadius"
          class="text-very-high-risk"
          stroke="currentColor"
          stroke-width="15"
          fill="transparent"
          :stroke-dasharray="veryHighRiskDashArray.join(',')"
          :transform="veryHighRiskRotation"
        />

        <circle
          :cx="donutCenter"
          :cy="donutCenter"
          :r="donutRadius"
          class="text-high-risk"
          stroke="currentColor"
          stroke-width="15"
          fill="transparent"
          :stroke-dasharray="highRiskDashArray.join(',')"
          :transform="highRiskRotation"
        />

        <circle
          :cx="donutCenter"
          :cy="donutCenter"
          :r="donutRadius"
          class="text-medium-risk"
          stroke="currentColor"
          stroke-width="15"
          fill="transparent"
          :stroke-dasharray="mediumRiskDashArray.join(',')"
          :transform="mediumRiskRotation"
        />

        <circle
          :cx="donutCenter"
          :cy="donutCenter"
          :r="donutRadius"
          class="text-low-risk"
          stroke="currentColor"
          stroke-width="15"
          fill="transparent"
          :stroke-dasharray="lowRiskDashArray.join(',')"
          :transform="lowRiskRotation"
        />

        <g>
          <text x="50%" y="57%" style="font-size: 24px; text-anchor: middle">
            {{ totalRiskPercents.totalNumber }}
          </text>
        </g>
      </svg>
    </MedTooltip>
  </button>

  <ComponentDialog
    ref="refDialog"
    dialog-title="Arzneimittel-Risiken (gesamt)"
    dialog-type="information"
    dialog-width="medium"
    :test-id="getTestId('risk-donut')"
  >
    <template #content>
      <p class="mt-0 mb-4">
        Angezeigt werden die Anzahl automatisiert detektierter Check-Treffer in den Farben rot (Risiken: sehr hoch),
        orange (Risiken: mittel-hoch), gelb (Risiken: gering-mittel) und grau (sonstige Risiken).
      </p>

      <p>
        Es wurden {{ totalRiskPercents.totalNumber }} Check-Treffer bei den folgenden {{ famsWithChecks }} FAMs
        gefunden:
      </p>

      <ul>
        <li
          v-for="(medication, index) in medicationRiskClassesWithRisks"
          :key="index"
          :data-test="getTestId(`risk-donut-dialog-medication-${index}`)"
        >
          <div>
            <div class="flex items-center space-x-2 p-2">
              <ComponentIcon class="mr-0 text-sm text-gray-500">fiber_manual_record</ComponentIcon>
              <span class="block overflow-hidden text-base">{{ medication.famName }}</span>
            </div>

            <ul class="mt-1 ml-7">
              <li
                v-for="(risk, riskIndex) in medication.risks"
                :key="riskIndex"
                class="mb-3 flex items-start space-x-2"
                :data-test="getTestId(`risk-donut-dialog-medication-${index}-risk-${riskIndex}`)"
              >
                <ComponentIcon class="mt-0.5 text-sm">remove</ComponentIcon>

                <span class="block overflow-hidden text-sm">
                  <span v-if="!embeddedInRow && risk.color === 'rot' && !configData.veryhighrisk">
                    {{ risk.checkName }},
                  </span>
                  <span v-else-if="!embeddedInRow && risk.color === 'orange' && !configData.highrisk">
                    {{ risk.checkName }},
                  </span>
                  <span v-else-if="!embeddedInRow && risk.color === 'gelb' && !configData.midrisk">
                    {{ risk.checkName }},
                  </span>
                  <span v-else-if="!embeddedInRow && risk.color === 'grau' && !configData.lowrisk">
                    {{ risk.checkName }},
                  </span>
                  <span
                    v-else-if="!embeddedInRow"
                    role="button"
                    tabindex="0"
                    class="cursor-pointer underline"
                    @click="goToAnchor(risk.id, risk.riskClass)"
                    @keydown.enter="goToAnchor(risk.id, risk.riskClass)"
                    @keydown.space.prevent="goToAnchor(risk.id, risk.riskClass)"
                  >
                    {{ risk.checkName }},
                  </span>
                  <span v-else-if="embeddedInRow">{{ risk.checkName }},</span>

                  <span v-if="risk.color === 'rot'">
                    Risiken: sehr hoch
                    <span class="text-red-600">(rot)</span>
                    : {{ risk.count }} Treffer
                  </span>

                  <span v-if="risk.color === 'orange'">
                    Risiken: mittel-hoch
                    <span class="text-orange-500">(orange)</span>
                    : {{ risk.count }} Treffer
                  </span>

                  <span v-if="risk.color === 'gelb'">
                    Risiken: gering-mittel
                    <span class="text-yellow-500">(gelb)</span>
                    : {{ risk.count }} Treffer
                  </span>

                  <span v-if="risk.color === 'grau'">
                    Risiken: sonstige
                    <span class="text-gray-500">(grau)</span>
                    : {{ risk.count }} Treffer
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <p v-if="showSubTitle" class="mt-6">Zu folgenden FAMs wurden keine Check-Treffer gefunden:</p>

      <ul class="mt-2">
        <li
          v-for="(medication, index) in medicationRiskClassesWithoutRisks"
          :key="index"
          class="mb-2 flex items-center p-2"
          :data-test="getTestId(`risk-donut-dialog-medication-without-risk-${index}`)"
        >
          <ComponentIcon class="mr-0 text-sm text-gray-500">fiber_manual_record</ComponentIcon>
          <span class="block overflow-hidden pl-2 text-base">{{ medication.famName }}</span>
        </li>
      </ul>
    </template>
  </ComponentDialog>
</template>
